import type { Result } from "neverthrow";
import { match } from "ts-pattern";
import type { Writable } from "type-fest";

import type { InvalidError } from "@thalamos/common";

import type { SubmissionDocumentUnbranded } from "../../assessments/document.js";
import {
  NOT_ANSWERED,
  submissionSchemaFor,
  type UnansweredItemTreatment,
} from "../../assessments/notAnswered.js";
import type { PublishedInstrumentSchema } from "../../assessments/publishedSchema.js";
import { pointerFor } from "../../assessments/validate.js";
import { judgedAnswersIn, problemsIn } from "./checkDocument.js";
import type { FormContext } from "./checks.js";
import type { FormElement } from "./plainForm.js";

type ItemElement = Extract<FormElement, { kind: "item" }>;

type GroupElement = Extract<FormElement, { kind: "group" }>;

/** Where an item stands against its form. */
export type ItemProgress = {
  readonly item: string;
  /** Whether `asking.required` is other than `no`, so the item counts in its group's progress. */
  readonly required: boolean;
  /** Rated with a response the instrument accepts, whether or not the item is done. */
  readonly answered: boolean;
  /** Answered, with no problem left at the item, its response or its comment. */
  readonly done: boolean;
};

export type GroupState =
  "not_opened" | "not_started" | "in_progress" | "completed";

/** Where a group stands, following from its items and whether it has been opened. */
export type GroupProgress = {
  readonly group: string;
  readonly state: GroupState;
  /** How many of the required items are done. */
  readonly done: number;
  /** How many items are required. */
  readonly total: number;
  /** How many answered items lack the comment the form requires of them. */
  readonly commentsMissing: number;
  readonly items: readonly ItemProgress[];
  /**
   * Whether the group counts towards readiness. A group the instrument
   * requires always does; an optional one once the document holds it or a
   * rule asks it.
   */
  readonly included: boolean;
};

/** Where a whole document stands against its form. */
export type AssessmentProgress = {
  /** Each group the form asks, in the form's order, included or not. */
  readonly groups: readonly GroupProgress[];
  readonly completed: number;
  /** The groups that count towards readiness. */
  readonly included: number;
  /** How many items the included groups ask. */
  readonly asked: number;
  /** How many of those are answered. */
  readonly answered: number;
  /** How many answered items lack the comment the form requires of them. */
  readonly commentsMissing: number;
  /**
   * Whether the document may be sent: every included group completed and
   * nothing wrong outside them, true where no group is included; or, where
   * unanswered items are sent as not answered, something answered, nothing
   * wrong with any answer given, and every domain carried over opened.
   */
  readonly ready: boolean;
};

const stateOf = (begun: boolean, complete: boolean): GroupState => {
  if (!begun) {
    return "not_started";
  }

  return complete ? "completed" : "in_progress";
};

/**
 * Whether a group counts towards readiness. A group the instrument requires
 * always does; an optional one once the document holds it, something in it is
 * answered, or a rule asks it.
 */
const countsTowardsReadiness = ({
  element,
  begun,
  document,
}: {
  element: GroupElement;
  begun: boolean;
  document: SubmissionDocumentUnbranded;
}): boolean =>
  element.asking.required !== "no" ||
  begun ||
  document[element.at.group] !== undefined ||
  typeof element.asking.when === "function";

/**
 * The document as sent where unanswered items are sent as not answered: every
 * item the form asks, in every group counting towards readiness, that has no
 * response holds {@link NOT_ANSWERED}. An item the form does not ask is left
 * alone, so a domain whose opening item is not answered stays closed and
 * nothing in it is sent. Errs only where no validator can be built from the
 * schema.
 */
export const withUnansweredSentAsNotAnswered = ({
  schema,
  form,
  document,
  context,
}: {
  schema: PublishedInstrumentSchema;
  form: readonly FormElement[];
  document: SubmissionDocumentUnbranded;
  context: FormContext;
}): Result<SubmissionDocumentUnbranded, InvalidError> =>
  judgedAnswersIn({ schema, form, document, context }).map(
    ({ groups: unasked, items: unaskedItems, answers }) => {
      const sent: Writable<SubmissionDocumentUnbranded> = { ...document };

      for (const element of form) {
        if (element.kind !== "group" || unasked.has(element.at.group)) {
          continue;
        }

        const { group } = element.at;
        const asked = form.flatMap((candidate) =>
          candidate.kind === "item" &&
          candidate.at.group === group &&
          !unaskedItems.has(candidate.at.item)
            ? [candidate.at.item]
            : [],
        );
        const begun = asked.some((item) => answers.has(item));

        if (!countsTowardsReadiness({ element, begun, document })) {
          continue;
        }

        for (const item of asked) {
          if (document[group]?.[item] === undefined) {
            sent[group] = {
              ...sent[group],
              [item]: { response: NOT_ANSWERED },
            };
          }
        }
      }

      return sent;
    },
  );

/**
 * How far a document, part-filled or complete, has got against its decorated
 * form. Nobody sets a state: an item is done when it is answered, any comment
 * the form requires is given, and every check on its answer passes; a group
 * is completed once something in it is rated, with every required item done
 * and nothing wrong at any item; a document is ready when every included
 * group is completed.
 *
 * Where `opened` is given, an included group not in it is not opened, whatever
 * its answers say, and holds readiness back like any other group short of
 * completed: a document that started with answers someone else gave is not
 * ready until its assessor has looked at every part of it. A group that does
 * not count keeps its ordinary state, since nothing waits on it.
 *
 * An item counts where the form requires it, as {@link problemsIn} does, so
 * an optional item left unrated holds nothing back. A group with no required
 * item still starts not started, holding readiness back until something in it
 * is rated: a domain nobody has looked at is not completed. Groups and items
 * the form does not ask are left out. A group the instrument makes optional is
 * listed whenever it is asked, but counts towards readiness only once the
 * document holds it or a rule asks it: one asked unconditionally and left out
 * is a document the instrument accepts. A problem outside every item also
 * holds readiness back, so a document is never ready while the server would
 * refuse it.
 *
 * Where unanswered items are sent as not answered, progress is judged on the
 * document as {@link withUnansweredSentAsNotAnswered} would send it, so an
 * item left unanswered troubles nothing, and the document is ready once
 * something is answered, every answer given is done, and every domain carried
 * over opened. Errs only where no
 * validator can be built from the schema.
 */
export const progressOf = (
  args: ProgressArgs & {
    /** What sending does with an item nobody answered; `refused` where absent. */
    unansweredItemTreatment?: UnansweredItemTreatment | undefined;
  },
): Result<AssessmentProgress, InvalidError> =>
  match(args.unansweredItemTreatment ?? "refused")
    .with("refused", (unansweredItemTreatment) =>
      progressAgainst({ ...args, unansweredItemTreatment }),
    )
    .with("sent_not_answered", (unansweredItemTreatment) =>
      withUnansweredSentAsNotAnswered(args).andThen((sent) =>
        progressAgainst({
          ...args,
          schema: submissionSchemaFor({
            schema: args.schema,
            unansweredItemTreatment,
          }),
          document: sent,
          unansweredItemTreatment,
        }),
      ),
    )
    .exhaustive();

type ProgressArgs = {
  schema: PublishedInstrumentSchema;
  form: readonly FormElement[];
  document: SubmissionDocumentUnbranded;
  context: FormContext;
  /** Where given, an included group outside it is `not_opened` whatever its answers say. */
  opened?: ReadonlySet<string> | undefined;
};

const progressAgainst = ({
  schema,
  form,
  document,
  context,
  opened,
  unansweredItemTreatment,
}: ProgressArgs & {
  unansweredItemTreatment: UnansweredItemTreatment;
}): Result<AssessmentProgress, InvalidError> =>
  judgedAnswersIn({ schema, form, document, context }).andThen(
    ({ groups: unasked, items: unaskedItems, answers }) =>
      problemsIn({ schema, form, document, context }).map((problems) => {
        // An item's comment is part of its question, so a problem with the
        // comment troubles the item.
        const troubled = ({ at, commentAt }: ItemElement): boolean =>
          [at, ...(commentAt === undefined ? [] : [commentAt])].some(
            ({ group, item }) => {
              const answerAt = pointerFor([group, item]);

              return problems.some(
                (problem) =>
                  problem.at === answerAt ||
                  problem.at.startsWith(`${answerAt}/`),
              );
            },
          );

        const commentMissing = ({ commentAt }: ItemElement): boolean =>
          commentAt !== undefined &&
          problems.some(
            (problem) =>
              problem.code === "must-have-a-comment" &&
              problem.at.startsWith(
                `${pointerFor([commentAt.group, commentAt.item])}/`,
              ),
          );

        const groups = form.flatMap((element): GroupProgress[] => {
          if (element.kind !== "group" || unasked.has(element.at.group)) {
            return [];
          }

          const { group } = element.at;
          const asked = form.flatMap((candidate) =>
            candidate.kind === "item" &&
            candidate.at.group === group &&
            !unaskedItems.has(candidate.at.item)
              ? [candidate]
              : [],
          );
          const items = asked.map((candidate): ItemProgress => {
            const { item } = candidate.at;
            const answered = answers.has(item);

            return {
              item,
              required: candidate.asking.required !== "no",
              answered,
              done: answered && !troubled(candidate),
            };
          });
          const required = items.filter((item) => item.required);
          const done = required.filter((item) => item.done).length;
          const begun = items.some((item) => item.answered);
          const untroubled = asked.every((candidate) => !troubled(candidate));
          const counts = countsTowardsReadiness({ element, begun, document });

          return [
            {
              group,
              state:
                counts && opened !== undefined && !opened.has(group)
                  ? "not_opened"
                  : stateOf(begun, done === required.length && untroubled),
              done,
              total: required.length,
              commentsMissing: asked.filter(commentMissing).length,
              items,
              included: counts,
            },
          ];
        });
        const included = groups.filter((group) => group.included);
        const completed = included.filter(
          ({ state }) => state === "completed",
        ).length;
        const askedItems = included.flatMap(({ items }) => items);
        const answered = askedItems.filter((item) => item.answered).length;

        return {
          groups,
          completed,
          included: included.length,
          asked: askedItems.length,
          answered,
          commentsMissing: included.reduce(
            (sum, group) => sum + group.commentsMissing,
            0,
          ),
          ready:
            problems.length === 0 &&
            match(unansweredItemTreatment)
              .with("refused", () => completed === included.length)
              .with(
                "sent_not_answered",
                () =>
                  answered > 0 &&
                  included.every(({ state }) => state !== "not_opened"),
              )
              .exhaustive(),
        };
      }),
  );
